import { Col, Row } from "react-grid-system";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoIncomeSubtool.module.css";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";
import {
  CMInfoUpdateSectionType,
  ValidationErrors,
} from "../../../../lib/api/models";
import { useState } from "react";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import {
  INCOME_SOURCE_MAX_LENGTH,
  partialSuccessErrorMessage,
} from "../../constants";

const strings = {
  incomeHeader: "Place of Employment / Source of Income",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  clear: "Clear",
  reset: "Reset",
  update: "Update",
};

type CMInfoIncomeSubtoolProps = {
  className?: string;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.EmploymentIncome];
  initialFormState: CMInfoFormInput[CMInfoSubtool.EmploymentIncome];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.EmploymentIncome]>,
  ) => void;
  validationMessages: ValidationErrors | undefined;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  startExpanded: boolean;
};

export default function CMInfoIncomeSubtool({
  className,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  validationMessages,
  onUpdate,
  currentlyUpdating,
  failedSections,
  startExpanded = false,
}: CMInfoIncomeSubtoolProps) {
  const [isExpanded, setIsExpanded] = useState(
    formState.incomeDueForVerification || startExpanded,
  );
  const [hasUnverifiedChanges, setHasUnverifiedChanges] = useState(false);

  return (
    <Collapsible
      disabled={
        formState.incomeDueForVerification || formState.isIncomeVerified
      }
      className={className}
      headerText={strings.incomeHeader}
      isExpanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <Row>
        <Col md={9}>
          <textarea
            className={styles.textarea}
            value={formState.source}
            onChange={(e) => {
              generalDispatch({
                source: e.currentTarget.value,
                isIncomeVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            maxLength={INCOME_SOURCE_MAX_LENGTH}
          />
          {validationMessages?.source && (
            <ValidationMessage
              className={styles.validationMessages}
              validationMessages={validationMessages.source}
            />
          )}
        </Col>
        <Col md={3}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.incomeLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isIncomeVerified}
            onCheck={(isIncomeVerified) =>
              generalDispatch({ isIncomeVerified })
            }
            isExpired={formState.incomeDueForVerification}
            disabled={hasUnverifiedChanges}
          />
        </Col>
      </Row>
      <Row>
        <Col className={styles.mt24}>
          <CMInfoSubtoolButtons
            hideScriptButton
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                generalDispatch(initialFormState);
                setHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!formState.isIncomeVerified}
            onClear={() =>
              onShowConfirmationModal("clear", () => {
                generalDispatch({
                  source: "",
                  isIncomeVerified: true,
                });
                setHasUnverifiedChanges(true);
              })
            }
            isClearDisabled={
              formState.source === "" &&
              (!formState.isIncomeVerified || hasUnverifiedChanges)
            }
            onUpdate={onUpdate}
            isUpdateDisabled={
              !formState.isIncomeVerified || !!currentlyUpdating
            }
            isUpdateLoading={
              currentlyUpdating === CMInfoSubtool.EmploymentIncome
            }
          />
        </Col>
      </Row>
      {failedSections.includes("income") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.incomeHeader),
              ]}
            />
          </Col>
        </Row>
      ) : null}
    </Collapsible>
  );
}
